import { useEffect, useState } from "react";
import { NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import { Menu, LogOut, X, ShieldCheck, PanelLeftClose, PanelLeftOpen, Moon, Sun } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "./PublicLayout";
import EmailVerifyBanner from "../security/EmailVerifyBanner";
import { BackButton } from "../pwa/PwaParts";

const SECURITY_PATH = { company: "/app/securite", admin: "/admin/securite", user: "/mon-espace/profil" };

const THEMES = {
  company: { aside: "bg-[#062A1D]", active: "bg-blue-500 text-white", idle: "text-teal-50/75 hover:bg-white/10 hover:text-white", tag: "Espace entreprise" },
  admin: { aside: "bg-indigo-950", active: "bg-indigo-400 text-indigo-950", idle: "text-indigo-100/80 hover:bg-white/10 hover:text-white", tag: "Administration" },
  user: { aside: "bg-slate-900", active: "bg-blue-400 text-slate-950", idle: "text-slate-300 hover:bg-white/10 hover:text-white", tag: "Espace usager" },
};

const SideNav = ({ nav, theme, onNavigate, collapsed }) => (
  <nav className="-mr-2 flex min-h-0 flex-col gap-1 overflow-y-auto pr-2" aria-label="Navigation principale">
    {nav.map(({ to, label, icon: Icon, end, id }) => (
      <NavLink key={id} to={to} end={end} onClick={onNavigate} data-testid={`sidebar-${id}-link`} title={collapsed ? label : undefined}
        className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${collapsed ? "justify-center" : ""} ${isActive ? theme.active : theme.idle}`}>
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" /><span className={collapsed ? "sr-only" : ""}>{label}</span>
      </NavLink>
    ))}
  </nav>
);

export default function DashboardShell({ variant, nav, headerSlot, subtitle, banner }) {
  const theme = THEMES[variant];
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("va_sidebar") === "1");
  const toggle = () => { localStorage.setItem("va_sidebar", collapsed ? "0" : "1"); setCollapsed(!collapsed); };
  const darkAllowed = variant === "company" || variant === "admin";
  const [dark, setDark] = useState(() => localStorage.getItem("va_theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("theme-dark", darkAllowed && dark);
    return () => { document.documentElement.classList.remove("theme-dark"); };
  }, [dark, darkAllowed]);
  const toggleDark = () => { localStorage.setItem("va_theme", dark ? "light" : "dark"); setDark(!dark); };
  const doLogout = async () => { await logout(); navigate("/connexion"); };

  const renderAside = (small) => (
    <div className={`flex h-full flex-col ${theme.aside} ${small ? "p-3" : "p-4"}`}>
      <div className="mb-2 px-2 pt-2"><Logo light compact={small} /></div>
      {!small && <p className="mb-6 px-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">{theme.tag}</p>}
      {subtitle && !small && <div className="mb-6 px-2">{subtitle}</div>}
      <SideNav nav={nav} theme={theme} collapsed={small} onNavigate={() => setOpen(false)} />
      <div className="mt-auto border-t border-white/10 px-2 pt-4">
        {!small && <p className="truncate text-sm font-medium text-white" data-testid="sidebar-user-name">{user?.name}</p>}
        {!small && <p className="truncate text-xs text-white/60">{user?.email}</p>}
        <Link to={SECURITY_PATH[variant]} onClick={() => setOpen(false)} data-testid="security-link" title="Sécurité & sessions"
          className="mt-3 flex items-center gap-2 text-xs font-medium text-white/70 transition-colors hover:text-white">
          <ShieldCheck className="h-3.5 w-3.5" /><span className={small ? "sr-only" : ""}>Sécurité & sessions</span>
        </Link>
        <button onClick={doLogout} data-testid="logout-button" title="Déconnexion"
          className="mt-2 flex items-center gap-2 text-xs font-medium text-white/70 transition-colors hover:text-white">
          <LogOut className="h-3.5 w-3.5" /><span className={small ? "sr-only" : ""}>Déconnexion</span>
        </button>
      </div>
    </div>
  );
  const aside = renderAside(false);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className={`sticky top-0 hidden h-screen shrink-0 transition-[width] duration-200 lg:block ${collapsed ? "w-20" : "w-64"}`} data-testid="sidebar">{renderAside(collapsed)}</aside>
      {open && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div className="w-72 max-w-[85vw]" style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>{aside}</div>
          <button className="flex-1 bg-black/40" onClick={() => setOpen(false)} aria-label="Fermer" data-testid="sidebar-close" />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-slate-200 bg-white/85 px-4 backdrop-blur-xl sm:px-6 [html.theme-dark_&]:bg-[#0B1510]/90">
          <div className="flex items-center gap-1">
            <button className="grid h-11 w-11 place-items-center rounded-full lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open" aria-label="Menu">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <BackButton home={{ company: "/app", admin: "/admin", user: "/mon-espace" }[variant]} />
          </div>
          <button className="hidden rounded-md p-1.5 text-slate-600 hover:bg-slate-100 lg:inline-flex" onClick={toggle} data-testid="sidebar-collapse-toggle"
            aria-label={collapsed ? "Déplier le menu" : "Replier le menu"} aria-expanded={!collapsed}>
            {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>
          <div className="ml-auto flex items-center gap-3">
            {darkAllowed && (
              <button onClick={toggleDark} className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100" data-testid="theme-toggle"
                aria-label={dark ? "Passer en thème clair" : "Passer en thème sombre"} aria-pressed={dark} title={dark ? "Thème clair" : "Thème sombre"}>
                {dark ? <Sun className="h-5 w-5 text-indigo-400" /> : <Moon className="h-5 w-5" />}
              </button>
            )}
            {headerSlot}
          </div>
        </header>
        <EmailVerifyBanner />
        {banner}
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6 lg:px-8"><Outlet /></main>
      </div>
    </div>
  );
}
